import { useInfiniteQuery } from '@tanstack/react-query';
import { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';

import InfiniteLoader from '@/components/InfiniteLoader';
import InfoTooltip from '@/components/InfoTooltip';
import { SectionHeading, SrOnly } from '@/components/Layout/Parts';
import { ResponsiveTable, Table, TableContent, Th } from '@/components/Table';
import useUser from '@/hooks/useUser';
import {
  FreelancerHolidayRequestResource,
  PaginatedResponse,
} from '@/types/resources';
import { api } from '@/utils/api';
import { VacationStatus } from '@/utils/enums';

import ApprovalsTableRow from './ApprovalsTableRow';

const StyledTable = styled(Table)<{ $isPending: boolean }>`
  th {
    font-size: 11px;
  }
  th,
  td {
    padding: 19px 16px;
    font-size: 12px;
    font-weight: 700;
    &:nth-child(1) {
      padding-left: 24px;
    }
    &:last-child {
      width: 56px;
      height: 64px;
    }
  }
  ${({ $isPending }) =>
    $isPending &&
    css`
      td {
        &:last-child {
          width: 56px;
          height: 64px;
          position: relative;
          padding-right: 16px;
          padding-left: 16px;
          border-left: 1px solid ${({ theme }) => theme.colors.grayscale} !important;
        }
      }
    `}
`;

interface Props {
  isPending?: boolean;
}

const ApprovalsTable: FC<Props> = ({ isPending = false }) => {
  const user = useUser();
  const fetchRequests = ({
    pageParam = '/v1/payments/freelancers/holidays/requests',
  }) => {
    return api.get<never, PaginatedResponse<FreelancerHolidayRequestResource>>(
      pageParam,
      {
        params: {
          'filter[freelancer_user_id]': user?.id,
          ...(isPending && { 'filter[status]': VacationStatus.Pending }),
          ...(!isPending && { 'filter[not_pending]': 1 }),
        },
      },
    );
  };

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<FreelancerHolidayRequestResource>>(
    [
      'freelancer_vacation_requests',
      {
        freelancer_user_id: user?.id,
        ...(isPending && { status: VacationStatus.Pending }),
        ...(!isPending && { status: 'Not Pending' }),
      },
    ],
    fetchRequests,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const requests = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<FreelancerHolidayRequestResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  return (
    <div>
      <SectionHeading>
        {isPending ? 'Pending approvals' : 'Past requests'}
      </SectionHeading>
      <ResponsiveTable>
        <StyledTable $isPending={isPending}>
          <thead>
            <tr>
              <Th>Request Date</Th>
              <Th>Vacation Date</Th>
              <Th>Deducted Days</Th>
              <Th>
                Type
                <InfoTooltip
                  title="Type"
                  content="Paid leave uses your available balance. Unpaid leave is uncompensated"
                />
              </Th>
              <Th>Team Lead Approval</Th>
              <Th>HR Approval</Th>
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={7}
              isError={isError}
              hasData={!!requests && requests.length > 0}
            >
              {requests?.map((request) => (
                <ApprovalsTableRow
                  key={request.id}
                  request={request}
                  isPending={isPending}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </div>
  );
};

export default ApprovalsTable;
